/**
 * useCompatFlow — state machine for the 8-screen Compatibility Check flow.
 *
 * States:
 *   idle → searching → found | not_found
 *   found → preview → paying → computing → done
 *   not_found → (invite sent; stays not_found until partner joins)
 *   Any state → error (via ERROR event)
 *
 * This hook does NOT call useSkuInvoice — payment is owned by CC5Payment.
 * It only tracks the abstract flow state via useReducer.
 *
 * Under 200 lines, fully typed, no `any`.
 */

import { useReducer } from 'react';

// --------------------------------------------------------------------------
// Public types
// --------------------------------------------------------------------------

export type CompatFlowState =
  | 'idle'
  | 'searching'
  | 'found'
  | 'not_found'
  | 'preview'
  | 'paying'
  | 'computing'
  | 'done'
  | 'sharing'
  /** Gift mode terminal: payment done, share-link ready (result goes to the recipient). */
  | 'gift_sent'
  | 'error';

export interface CompatPartner {
  id: string;
  displayName: string;
  username: string;
  archetype: string;
  big3: { sun: string; moon: string; rising: string };
  mutualFriendsCount: number;
  /** Glyph used for avatar display (derived from archetype or default) */
  glyph: string;
  /** Palette for CCAvatar */
  palette: 'gold' | 'violet' | 'rose' | 'blue';
}

export interface CompatRunOutput {
  scoreLove: number;
  scoreFriendship: number;
  scoreCommunication: number;
  scoreConflict: number;
  scoreOverall: number;
  reportMarkdown: string;
  reportLanguage: string;
  llmCostUsd: number;
}

export interface CompatFlowData {
  state: CompatFlowState;
  searchUsername: string;
  partner: CompatPartner | null;
  output: CompatRunOutput | null;
  error: string | null;
  /** True when partner was reached via invite flow (CC3Invite) */
  wasInvited: boolean;
  inviteLink: string | null;
  /** Server-issued CompatInvite id — required by purchase pipeline (Wave D #2b) */
  compatInviteId: string | null;
  /**
   * Gift mode (gifting_crush): buy an anonymous compat gift for someone else.
   * The reveal is computed for and delivered to the RECIPIENT (the buyer never
   * sees it in-app), so after PAID the flow lands on `gift_sent` (share link),
   * not `computing`/`done`. Set once at flow init from the route, never mutated.
   */
  isGift: boolean;
}

// --------------------------------------------------------------------------
// Events
// --------------------------------------------------------------------------

export type CompatEvent =
  | { type: 'SEARCH'; username: string }
  | { type: 'FOUND'; partner: CompatPartner }
  | { type: 'NOT_FOUND' }
  | { type: 'CONFIRM' }
  | { type: 'PAY' }
  | { type: 'PAID' }
  | { type: 'RUN_COMPLETE'; output: CompatRunOutput }
  | { type: 'SHARE' }
  | { type: 'CANCEL' }
  | { type: 'RETRY' }
  | { type: 'ERROR'; message: string }
  | { type: 'SET_INVITE_LINK'; link: string }
  | { type: 'SET_INVITE_ID'; id: string }
  | { type: 'MARK_INVITED' };

// --------------------------------------------------------------------------
// Reducer
// --------------------------------------------------------------------------

const INITIAL: CompatFlowData = {
  state: 'idle',
  searchUsername: '',
  partner: null,
  output: null,
  error: null,
  wasInvited: false,
  inviteLink: null,
  compatInviteId: null,
  isGift: false,
};

function compatReducer(data: CompatFlowData, event: CompatEvent): CompatFlowData {
  switch (event.type) {
    case 'SEARCH':
      return { ...data, state: 'searching', searchUsername: event.username, error: null };

    case 'FOUND':
      return { ...data, state: 'found', partner: event.partner };

    case 'NOT_FOUND':
      // Gift mode: an off-platform recipient (not on Calestea yet) can still be
      // gifted — go straight to the pay preview instead of the join-only CC3Invite.
      // They register + consent via the share link in the bot afterward, exactly
      // like the bot's /gift to an unknown @username.
      return { ...data, state: data.isGift ? 'preview' : 'not_found', partner: null };

    case 'CONFIRM':
      if (data.state !== 'found') return data;
      return { ...data, state: 'preview' };

    case 'PAY':
      if (data.state !== 'preview') return data;
      return { ...data, state: 'paying' };

    case 'PAID':
      if (data.state !== 'paying') return data;
      // Gift: the reveal is computed for the recipient (often only after they
      // accept the invite in the bot), so there is nothing to show the buyer —
      // land on the share-link terminal instead of polling for a result.
      return { ...data, state: data.isGift ? 'gift_sent' : 'computing' };

    case 'RUN_COMPLETE':
      if (data.state !== 'computing') return data;
      return { ...data, state: 'done', output: event.output };

    case 'SHARE':
      if (data.state !== 'done') return data;
      return { ...data, state: 'sharing' };

    case 'CANCEL':
      // Cancel from paying → go back to preview
      if (data.state === 'paying') return { ...data, state: 'preview' };
      return data;

    case 'RETRY':
      // Retry from error → go back to idle, preserving the gift/non-gift mode
      return { ...INITIAL, isGift: data.isGift };

    case 'ERROR':
      return { ...data, state: 'error', error: event.message };

    case 'SET_INVITE_LINK':
      return { ...data, inviteLink: event.link };

    case 'SET_INVITE_ID':
      return { ...data, compatInviteId: event.id };

    case 'MARK_INVITED':
      return { ...data, wasInvited: true };

    default:
      return data;
  }
}

// --------------------------------------------------------------------------
// Hook
// --------------------------------------------------------------------------

export interface UseCompatFlowReturn extends CompatFlowData {
  send: (event: CompatEvent) => void;
}

export function useCompatFlow(isGift = false): UseCompatFlowReturn {
  const [data, dispatch] = useReducer(compatReducer, { ...INITIAL, isGift });

  return {
    ...data,
    send: dispatch,
  };
}
